// TelcoTrack — koláče ziskovosti stavby (příjem vs výdej po položkách).

import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as echarts from 'echarts';
import type { ECharts } from 'echarts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ThemeService } from '../../../core/services/theme.service';
import { buildFinanceProfitabilityChartOption } from '../../utils/finance-profitability-chart.echarts';
import { prefersReducedMotion } from '../../utils/stat-motion';
import type { FinanceProfitabilityChartRow } from '../../utils/finance-profitability-chart.util';

@Component({
  selector: 'app-finance-profitability-chart',
  templateUrl: './finance-profitability-chart.component.html',
  styleUrls: ['./finance-profitability-chart.component.scss'],
})
export class FinanceProfitabilityChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private chart: ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeRafId = 0;
  private lastResizeObservedW = -1;
  private lastResizeObservedH = -1;
  private chartThemeIsDark = false;

  @ViewChild('chartHost') chartHost?: ElementRef<HTMLDivElement>;

  @Input() rows: FinanceProfitabilityChartRow[] = [];
  @Input() ariaLabel = 'Koláče příjmu a výdeje stavby po položkách';

  constructor(private readonly themeService: ThemeService) {
    this.themeService.currentTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe((theme) => {
        this.chartThemeIsDark = theme === 'dark';
        this.refreshOptionIfReady();
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.ensureChart(), 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows']) {
      setTimeout(() => this.ensureChart(), 0);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);
    this.teardown();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshOptionIfReady(): void {
    if (this.chart && this.rows.length) this.applyOption();
  }

  private ensureChart(): void {
    const el = this.chartHost?.nativeElement;
    if (!el || !this.rows.length) {
      this.teardown();
      return;
    }
    this.chart ??= echarts.init(el, undefined, { renderer: 'canvas' });
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const w = Math.round(entry.contentRect.width);
        const h = Math.round(entry.contentRect.height);
        if (w === this.lastResizeObservedW && h === this.lastResizeObservedH) return;
        this.lastResizeObservedW = w;
        this.lastResizeObservedH = h;
        if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);
        this.resizeRafId = requestAnimationFrame(() => {
          this.resizeRafId = 0;
          this.applyOption();
          this.chart?.resize();
        });
      });
      this.resizeObserver.observe(el);
    }
    this.applyOption();
    this.chart.resize();
  }

  private applyOption(): void {
    if (!this.chart || !this.rows.length) return;
    const el = this.chartHost?.nativeElement;
    const option = buildFinanceProfitabilityChartOption({
      rows: this.rows,
      dark: this.chartThemeIsDark,
      width: el?.clientWidth || 960,
      height: el?.clientHeight || 420,
      reduceMotion: prefersReducedMotion(),
    });
    if (!option) return;
    this.chart.setOption(option, { notMerge: true, lazyUpdate: false });
  }

  private teardown(): void {
    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = 0;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
  }
}
